import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import Done from '@components/icons/Done.svg';
import EditIcon from '@components/icons/Edit.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FeatureFlagsService, PrismaFeatureFlagDto } from 'app/API';
import { featureFlagsKeys } from 'app/queries/featureFlags/featureFlagsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { createFetchAttorneyViewFeatureFlagsQueryFn } from '../../api/fetchAttorneyViewFeatureFlags';
import { CellContent } from './styles/CellContent';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { Wrapper } from './styles/Wrapper';

export const Content = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const navigate = useNavigate();

  const flagsQuery = useQuery({
    queryKey: featureFlagsKeys.attorneyView(),
    queryFn: createFetchAttorneyViewFeatureFlagsQueryFn(call),
  });

  const removeMutation = useMutation({
    mutationFn: (featureFlagId: string) => call(() => FeatureFlagsService.removeForAttorneyView({ featureFlagId })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: featureFlagsKeys.attorneyView() }),
  });

  const refreshFlags = () => {
    void queryClient.invalidateQueries({ queryKey: featureFlagsKeys.attorneyView() });
  };

  const createFlag = () => {
    openModal(ModalNamesEnum.CreateNewAttorneyFeatureFlag, {
      onSuccess: () => {
        closeModal();
        refreshFlags();
      },
    });
  };

  const editFlag = (featureFlag: PrismaFeatureFlagDto) => {
    openModal(ModalNamesEnum.EditAttorneyFeatureFlag, {
      featureFlag,
      onSuccess: () => {
        closeModal();
        refreshFlags();
      },
    });
  };

  const removeFlag = (id: string) => {
    const confirmation = window.confirm('Are you sure you want to remove this feature flag?');

    if (!confirmation) {
      return;
    }
    removeMutation.mutate(id);
  };

  const flags = flagsQuery.data ?? [];
  const isLoading = flagsQuery.isFetching || removeMutation.isPending;

  return (
    <>
      <Helmet>
        <title>Feature Flags - AutoScript Admin</title>
      </Helmet>
      <Header
        title="Feature Flags"
        buttonLabel="Create new flag"
        buttonOnClick={() => createFlag()}
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              onClick={() => {
                navigate(routes.adminFeatureFlags.make());
              }}
            >
              ASWeb
            </SwitchButton>
            <SwitchButton
              active
              onClick={() => {
                navigate(routes.adminAttorneyViewFeatureFlags.make());
              }}
            >
              AttorneyView
            </SwitchButton>
          </HeaderSwitchWrapper>,
        ]}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Name</Th>
                <Th>Value</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {flags.map((data, index) => (
                <Tr key={data.id}>
                  <Td>
                    <CellContent>{index + 1}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.name}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.value ? <Done width="24px" /> : ''}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>
                      <div className="flex gap-1">
                        <IconButton title="Edit" onClick={() => editFlag(data)}>
                          <EditIcon />
                        </IconButton>
                        <IconButtonDelete title="Remove" onClick={() => removeFlag(data.id)}>
                          <TrashBin />
                        </IconButtonDelete>
                      </div>
                    </CellContent>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
      </Wrapper>
    </>
  );
};
